/**
 * Salary hub island loader: hydrates the server-rendered tree in place (indexable-page hydration
 * rework, following the SRP/job fix). layouts/LayoutDefault.tsx's salary-hub branch now
 * server-renders SalaryIndexPage wrapped in the SAME full provider stack this file builds
 * (Provider/store, AuthProvider, FeedbackModalProvider, ToastProvider, SearchProvider,
 * SearchWrapper) directly into #salary-ssr — there's no separate #salary-app to mount a new tree
 * into and swap in anymore, so this file just calls hydrateRoot() on #salary-ssr.
 */
import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';
import { Provider } from 'react-redux';
import { IntlProvider } from 'react-intl';
import type { PageContextClient } from 'vike/types';

import { getReactRootSentryOptions } from '../../utils/react/react-root-sentry-options';
import { loadLocaleData } from '../../utils/routing/url-helpers';
import { buildFooterStaticStrings } from '../../utils/footer/buildFooterStaticStrings';
import { createStore } from '../../redux/createStore';
import { AuthProvider } from '../../contexts/AuthContext';
import { ExperimentProvider } from '../../contexts/ExperimentContext';
import { UserProvider } from '../../query/UserProvider';
import { FeedbackModalProvider } from '../../contexts/FeedbackModalContext';
import { ToastProvider } from '../../contexts/ToastContext';
import { FooterStringsProvider } from '../../contexts/FooterStringsContext';
import { ToastContainer } from '../../design-system/Toast';
import { SearchProvider } from '../../contexts/SearchContext';
import { SearchWrapper } from '../../layouts/SearchWrapper';
import { getIndexName } from '../../constants';
import {
  scheduleIdleCallbackWithFallback,
  cancelIdleScheduleHandle,
  type IdleScheduleHandle,
} from '../../utils/browser/scheduleIdleCallbackWithFallback';

/** Worst-case cap (ms) for the deferred hydrate path — see srp.client.ts's constant of the same name. */
const DEFER_MOUNT_FALLBACK_MS = 2500;

let salaryRoot: Root | null = null;
let salaryRootContainer: HTMLElement | null = null;

const SUPPORTED_LOCALES = ['en', 'fi', 'sv'] as const;

function getLocaleFromPathname(pathname: string): string {
  const first = pathname.split('/').filter(Boolean)[0]?.toLowerCase();
  return first && (SUPPORTED_LOCALES as readonly string[]).includes(first) ? first : 'en';
}

/**
 * Unmount the salary-hub island root synchronously. Must be called by `+onRenderClient.ts`
 * before it creates/renders a *different* React root over `#root` (an ancestor of
 * `#salary-ssr`) — same ordering reason as the other island loaders.
 */
export function unmountSalaryIsland(): void {
  if (salaryRoot) {
    salaryRoot.unmount();
    salaryRoot = null;
    salaryRootContainer = null;
  }
}

export interface RunSalaryIslandLoaderOptions {
  /** When true (e.g. we skipped root hydration), hydrate immediately instead of deferring. */
  mountImmediately?: boolean;
}

async function hydrateSalaryIsland(container: HTMLElement, pageContext: PageContextClient): Promise<void> {
  const pathname =
    typeof window !== 'undefined' ? window.location.pathname : ((pageContext as any)?.urlPathname ?? '/');
  const locale = (pageContext?.locale as string) || getLocaleFromPathname(pathname) || 'en';
  const messages = loadLocaleData(locale);
  const footerStrings = buildFooterStaticStrings(messages);
  const store = createStore(pageContext);
  const initialUser = (pageContext?.user ?? null) as any;
  const initialHasBackofficeAccess = Boolean((pageContext as { hasBackofficeAccess?: boolean })?.hasBackofficeAccess);
  const initialExperiments = (pageContext as { experiments?: Record<string, string> })?.experiments ?? {};

  const { default: SalaryIndexPage } = await import('./+Page');

  const tree = React.createElement(
    Provider,
    { store },
    React.createElement(
      IntlProvider,
      { messages, locale, defaultLocale: 'en' },
      React.createElement(
        FooterStringsProvider,
        { strings: footerStrings },
        React.createElement(
          AuthProvider,
          { initialUser, initialHasBackofficeAccess },
          React.createElement(
            ExperimentProvider,
            { initialVariants: initialExperiments },
          React.createElement(
            UserProvider,
            { initialUser, initialHasBackofficeAccess },
            React.createElement(
              FeedbackModalProvider,
              null,
              React.createElement(
                ToastProvider,
                null,
                React.createElement(
                  React.Fragment,
                  null,
                  React.createElement(ToastContainer),
                  React.createElement(
                    SearchProvider,
                    {
                      indexName: getIndexName(locale),
                      initialQuery: '',
                      initialFilters: {},
                      initialPage: 0,
                      initialResults: null,
                      initialSort: 'relevance',
                      syncToUrl: false,
                      localeOverride: locale,
                    },
                    React.createElement(SearchWrapper, { isSearchPage: false }, React.createElement(SalaryIndexPage)),
                  ),
                ),
              ),
            ),
          ),
          ),
        ),
      ),
    ),
  );

  if (salaryRoot && salaryRootContainer === container) {
    salaryRoot.render(tree);
    return;
  }

  if (salaryRoot && salaryRootContainer) {
    salaryRoot.unmount();
    salaryRoot = null;
    salaryRootContainer = null;
  }

  // hydrateRoot, not createRoot: LayoutDefault.tsx already server-rendered this exact tree into
  // `container` — this attaches event listeners to the existing DOM instead of building and
  // painting a new one. getReactRootSentryOptions() wires onRecoverableError to Sentry.
  salaryRoot = hydrateRoot(container, tree, getReactRootSentryOptions());
  salaryRootContainer = container;
}

export function runSalaryIslandLoader(
  pageContext: PageContextClient,
  options: RunSalaryIslandLoaderOptions = {},
): void {
  const appEl = document.getElementById('salary-ssr');
  if (!appEl) return;

  let mounted = false;
  let fallbackHandle: IdleScheduleHandle | null = null;

  const doMount = (): void => {
    if (mounted) return;
    mounted = true;
    if (fallbackHandle !== null) {
      cancelIdleScheduleHandle(fallbackHandle);
      fallbackHandle = null;
    }
    void hydrateSalaryIsland(appEl, pageContext);
  };

  const onInteraction = (): void => {
    doMount();
    document.removeEventListener('pointerdown', onInteraction);
    document.removeEventListener('keydown', onInteraction);
    document.removeEventListener('focus', onInteraction, true);
  };

  document.addEventListener('pointerdown', onInteraction, { once: true, passive: true });
  document.addEventListener('keydown', onInteraction, { once: true, passive: true });
  document.addEventListener('focus', onInteraction, { once: true, capture: true });

  if (options.mountImmediately) {
    doMount();
  } else {
    fallbackHandle = scheduleIdleCallbackWithFallback(doMount, { timeout: DEFER_MOUNT_FALLBACK_MS });
  }
}
